import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Label,
  ConfirmDialog,
  ResultDialog,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { blockchainNetworksApi } from "@/services/api";
import {
  Link2,
  Plus,
  Plug,
  CheckCircle,
  XCircle,
  Loader2,
  Star,
  MoreHorizontal,
  Pencil,
  Trash2,
  Power,
  X,
} from "lucide-react";

interface BlockchainNetwork {
  id: string;
  name: string;
  chainId: string;
  type: "MAINNET" | "TESTNET" | "LOCAL";
  status: "ACTIVE" | "INACTIVE";
  isDefault: boolean;
  explorerUrl?: string;
  currencySymbol?: string;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  id: string;
  name: string;
  rpcUrl: string;
  chainId: string;
  type: "MAINNET" | "TESTNET" | "LOCAL";
  explorerUrl: string;
  currencySymbol: string;
  isDefault: boolean;
}

const initialFormData: FormData = {
  id: "",
  name: "",
  rpcUrl: "",
  chainId: "",
  type: "MAINNET",
  explorerUrl: "",
  currencySymbol: "XDC",
  isDefault: false,
};

const networkTypes = [
  { value: "MAINNET", label: "Mainnet" },
  { value: "TESTNET", label: "Testnet" },
  { value: "LOCAL", label: "Local" },
];

export function BlockchainNetworksPage() {
  const [networks, setNetworks] = useState<BlockchainNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    id: string;
    success: boolean;
    blockNumber?: number;
    latency?: number;
    error?: string;
  } | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState("");

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: "default" | "danger";
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirmar",
    variant: "default",
    onConfirm: () => {},
    loading: false,
  });

  // Result dialog state
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({
    open: false,
    variant: "error",
    title: "",
  });

  useEffect(() => {
    loadNetworks();
  }, []);

  async function loadNetworks() {
    try {
      const data = await blockchainNetworksApi.list();
      setNetworks(data);
    } catch (error) {
      console.error("Erro ao carregar redes:", error);
    } finally {
      setLoading(false);
    }
  }

  function openCreateModal() {
    setFormData(initialFormData);
    setEditingId(null);
    setFormError("");
    setShowModal(true);
  }

  function openEditModal(network: BlockchainNetwork) {
    setFormData({
      id: network.id,
      name: network.name,
      rpcUrl: "", // RPC URL is encrypted, user needs to re-enter
      chainId: network.chainId,
      type: network.type,
      explorerUrl: network.explorerUrl || "",
      currencySymbol: network.currencySymbol || "XDC",
      isDefault: network.isDefault,
    });
    setEditingId(network.id);
    setFormError("");
    setShowModal(true);
  }

  async function handleSave() {
    setFormError("");

    // Validations
    if (!formData.id || !formData.name || !formData.chainId) {
      setFormError("ID, Nome e Chain ID sao obrigatorios");
      return;
    }

    if (!editingId && !formData.rpcUrl) {
      setFormError("RPC URL e obrigatorio para novas redes");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const updateData: Record<string, unknown> = {
          name: formData.name,
          chainId: formData.chainId,
          type: formData.type,
          explorerUrl: formData.explorerUrl || undefined,
          currencySymbol: formData.currencySymbol || undefined,
        };
        // Only send RPC URL if provided
        if (formData.rpcUrl) {
          updateData.rpcUrl = formData.rpcUrl;
        }
        await blockchainNetworksApi.update(editingId, updateData);
      } else {
        await blockchainNetworksApi.create(formData);
      }
      setShowModal(false);
      await loadNetworks();
    } catch (error: any) {
      setFormError(error.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  async function handleTestConnection(id: string) {
    setTesting(id);
    setTestResult(null);
    try {
      const result = await blockchainNetworksApi.testConnection(id);
      setTestResult({ id, ...result });
    } catch (error) {
      setTestResult({
        id,
        success: false,
        error: error instanceof Error ? error.message : "Erro desconhecido",
      });
    } finally {
      setTesting(null);
    }
  }

  async function handleSetDefault(id: string) {
    try {
      await blockchainNetworksApi.setDefault(id);
      await loadNetworks();
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao definir padrão",
        description: error instanceof Error ? error.message : "Não foi possível definir a rede como padrão. Tente novamente.",
      });
    }
  }

  async function handleToggleStatus(network: BlockchainNetwork) {
    try {
      await blockchainNetworksApi.update(network.id, {
        status: network.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
      });
      await loadNetworks();
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao alterar status",
        description: error instanceof Error ? error.message : "Não foi possível alterar o status da rede. Tente novamente.",
      });
    }
  }

  function handleDelete(id: string) {
    setConfirmDialog({
      open: true,
      title: "Excluir Rede",
      description: "Tem certeza que deseja excluir esta rede? Esta acao nao pode ser desfeita.",
      confirmText: "Excluir",
      variant: "danger",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await blockchainNetworksApi.delete(id);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          await loadNetworks();
        } catch (error) {
          console.error("Erro ao excluir:", error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false,
    });
  }

  const stats = [
    { value: networks.length, label: "Redes" },
    { value: networks.filter((n) => n.type === "MAINNET").length, label: "Mainnet" },
    { value: networks.filter((n) => n.type === "TESTNET").length, label: "Testnet" },
  ];

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Link2 className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Redes Blockchain"
          description="Configuracao das redes blockchain para operacoes"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Link2 className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Redes Blockchain"
        description="Configuracao das redes blockchain para operacoes"
        stats={stats}
        actions={
          <Button onClick={openCreateModal} className="bg-white text-blue-600 hover:bg-blue-50">
            <Plus className="w-4 h-4 mr-2" />
            Nova Rede
          </Button>
        }
      />

      {/* Networks List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Redes Cadastradas</CardTitle>
        </CardHeader>
        <CardContent>
          {networks.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Nenhuma rede cadastrada. Clique em "Nova Rede" para adicionar.
            </p>
          ) : (
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">ID</th>
                    <th className="p-4 font-medium">Nome</th>
                    <th className="p-4 font-medium">Chain ID</th>
                    <th className="p-4 font-medium">Tipo</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {networks.map((network) => (
                    <tr
                      key={network.id}
                      className="border-b border-border last:border-0 hover:bg-muted/50"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <code className="text-sm bg-muted px-2 py-1 rounded">
                            {network.id}
                          </code>
                          {network.isDefault && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                      </td>
                      <td className="p-4">{network.name}</td>
                      <td className="p-4">
                        <code className="text-sm">{network.chainId}</code>
                      </td>
                      <td className="p-4">
                        <Badge
                          variant={
                            network.type === "MAINNET"
                              ? "default"
                              : network.type === "TESTNET"
                              ? "secondary"
                              : "outline"
                          }
                        >
                          {network.type}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge
                          variant={network.status === "ACTIVE" ? "success" : "destructive"}
                        >
                          {network.status === "ACTIVE" ? "Ativo" : "Inativo"}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTestConnection(network.id)}
                            disabled={testing === network.id}
                            title="Testar conexao"
                          >
                            {testing === network.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Plug className="w-4 h-4" />
                            )}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[160px]">
                              <DropdownMenuItem onClick={() => openEditModal(network)}>
                                <Pencil className="w-3 h-3 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              {!network.isDefault && (
                                <DropdownMenuItem onClick={() => handleSetDefault(network.id)}>
                                  <Star className="w-3 h-3 mr-2" />
                                  Definir como Padrao
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleToggleStatus(network)}>
                                <Power className="w-3 h-3 mr-2" />
                                {network.status === "ACTIVE" ? "Desativar" : "Ativar"}
                              </DropdownMenuItem>
                              {!network.isDefault && (
                                <DropdownMenuItem
                                  onClick={() => handleDelete(network.id)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="w-3 h-3 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Test Result */}
          {testResult && (
            <div
              className={`mt-4 p-4 rounded-lg flex items-center gap-3 ${
                testResult.success
                  ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                  : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
              }`}
            >
              {testResult.success ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <span className="font-medium">Conexao bem-sucedida!</span>
                    <span className="ml-2">
                      Bloco: {testResult.blockNumber?.toLocaleString()} | Latencia: {testResult.latency}ms
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5" />
                  <span>Falha na conexao: {testResult.error}</span>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingId ? "Editar Rede" : "Nova Rede Blockchain"}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="id">ID *</Label>
                  <Input
                    id="id"
                    placeholder="XDC_MAINNET"
                    value={formData.id}
                    onChange={(e) =>
                      setFormData({ ...formData, id: e.target.value.toUpperCase() })
                    }
                    disabled={!!editingId}
                    className={editingId ? "bg-muted" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    placeholder="XDC Network Mainnet"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rpcUrl">
                  RPC URL * {editingId && "(deixe vazio para manter atual)"}
                </Label>
                <Input
                  id="rpcUrl"
                  placeholder="https://erpc.xinfin.network"
                  value={formData.rpcUrl}
                  onChange={(e) => setFormData({ ...formData, rpcUrl: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="chainId">Chain ID *</Label>
                  <Input
                    id="chainId"
                    placeholder="50"
                    value={formData.chainId}
                    onChange={(e) => setFormData({ ...formData, chainId: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currencySymbol">Simbolo da Moeda</Label>
                  <Input
                    id="currencySymbol"
                    placeholder="XDC"
                    value={formData.currencySymbol}
                    onChange={(e) =>
                      setFormData({ ...formData, currencySymbol: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Tipo *</Label>
                <select
                  id="type"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type: e.target.value as "MAINNET" | "TESTNET" | "LOCAL",
                    })
                  }
                >
                  {networkTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="explorerUrl">URL do Explorer (opcional)</Label>
                <Input
                  id="explorerUrl"
                  placeholder="https://xdcscan.io"
                  value={formData.explorerUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, explorerUrl: e.target.value })
                  }
                />
              </div>

              {!editingId && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) =>
                      setFormData({ ...formData, isDefault: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-medium">Definir como rede padrao</span>
                </label>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingId ? "Salvar" : "Criar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
      />

      {/* Result Dialog */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
      />
    </div>
  );
}
